"use client";

import { useStudentRatings } from "@/api/hook/useStudentRating";
import Footer from "@/components/layout/Footer";
import Header from "@/components/layout/Header";
import { StudentCard } from "@/components/students-rating/StudentsRatingCard";
import { useState, useMemo } from "react";

function SkeletonCard() {
  return (
    <div className="p-6 flex flex-col items-center text-center bg-white rounded-lg shadow-md animate-pulse">
      <div className="mb-4 w-20 h-20 rounded-full bg-gray-200" />
      <div className="mb-2 h-4 w-24 bg-gray-200 rounded" />
      <div className="mb-4 h-3 w-20 bg-gray-200 rounded" />
      <div className="flex gap-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="w-4 h-4 bg-gray-200 rounded-full" />
        ))}
      </div>
    </div>
  );
}

export default function TopStudentsPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 12;

  const { data: allData, isLoading } = useStudentRatings(0, 1000);

  const topStudents = useMemo(() => {
    return allData?.content?.slice(0, 3) || [];
  }, [allData]);

  const filteredStudents = useMemo(() => {
    if (!allData?.content) return [];

    const query = searchQuery.toLowerCase();
    const allStudents = allData.content;

    return allStudents.filter((student) => {
      const firstName = student.student.firstName.toLowerCase();
      const lastName = student.student.lastName.toLowerCase();
      const middleName = (student.student.middleName || "").toLowerCase();
      const email = (student.student.email || "").toLowerCase();
      const phone = (student.student.phoneNumber || "").toLowerCase();

      return (
        firstName.includes(query) ||
        lastName.includes(query) ||
        middleName.includes(query) ||
        email.includes(query) ||
        phone.includes(query) ||
        `${firstName} ${lastName}`.includes(query) ||
        `${firstName} ${middleName} ${lastName}`.includes(query)
      );
    });
  }, [allData, searchQuery]);

  const paginatedStudents = useMemo(() => {
    const topIds = new Set(topStudents.map((s) => s.id));
    return filteredStudents.filter((student) => !topIds.has(student.id));
  }, [filteredStudents, topStudents]);

  const totalPages = Math.ceil(paginatedStudents.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentCards = paginatedStudents.slice(startIndex, endIndex);

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const showPages = Math.max(5, Math.min(totalPages, 7));

    let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
    const endPage = Math.min(totalPages, startPage + showPages - 1);

    if (endPage - startPage + 1 < showPages) {
      startPage = Math.max(1, endPage - showPages + 1);
    }

    if (startPage > 1) {
      pages.push(1);
      if (startPage > 2) pages.push("...");
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) pages.push("...");
      pages.push(totalPages);
    }

    return pages;
  };

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  if (isLoading) {
    return (
      <main>
        <Header />
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-8">
          <div className="max-w-6xl mx-auto">
            <h1 className="text-xl md:text-3xl lg:text-5xl font-bold text-center mb-4 text-[#21205A]">
              Our Top Student Ratings
            </h1>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {Array.from({ length: 8 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          </div>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main>
      <Header />
      <section className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-2xl md:text-3xl lg:text-5xl font-bold text-[#21205A] mb-2">
              Our Top Student Ratings
            </h1>
            <p className="text-gray-600 text-lg">
              Celebrating our most dedicated learners
            </p>
          </div>

          {/* Top 3 Students - Featured (Only visible when not searching) */}
          <div className="w-full">
            {!searchQuery && (
              <div className="w-full mb-12">
                <h1 className="text-2xl font-bold text-[#21205A] mb-2">
                  Our Most Outstanding Performers
                </h1>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                  {topStudents.map((student, index) => (
                    <div key={student.id}>
                      <StudentCard student={student} rank={index + 1} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Search Bar */}
          <div className="mb-8 w-full">
            <input
              type="text"
              placeholder="Search by name, phonenumber..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          {/* Paginated Results */}
          {!allData?.content || allData.content.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">No students found.</p>
            </div>
          ) : filteredStudents.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">
                No students found matching "{searchQuery}".
              </p>
            </div>
          ) : currentCards.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">No results on this page.</p>
            </div>
          ) : (
            <>
              <div>
                <h2 className="text-2xl font-bold text-[#21205A] mb-6">
                  {searchQuery ? "Search Results" : "More Top Performers"}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                  {currentCards.map((student, index) => (
                    <StudentCard
                      key={student.id}
                      student={student}
                      rank={startIndex + index + 4}
                    />
                  ))}
                </div>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8 flex-wrap">
                  {/* Previous Button */}
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={currentPage === 1}
                    className="px-3 py-2 border border-gray-300 rounded-md bg-primary text-[#FFF] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>

                  {/* Page Numbers */}
                  <div className="flex gap-1 flex-wrap justify-center">
                    {getPageNumbers().map((page, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          if (typeof page === "number") {
                            setCurrentPage(page);
                          }
                        }}
                        disabled={page === "..."}
                        className={`w-10 h-10 rounded border transition-colors ${
                          page === currentPage
                            ? "bg-primary text-white border-primary"
                            : page === "..."
                            ? "border-gray-300 text-gray-400 cursor-not-allowed"
                            : "border-gray-300 hover:border-primary hover:text-primary"
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>

                  {/* Next Button */}
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 border border-gray-300 rounded-md bg-[#0b8536] text-[#FFF] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              )}

              <div className="text-center mt-6 text-sm text-gray-600">
                Showing {startIndex + 1} to{" "}
                {Math.min(endIndex, paginatedStudents.length)} of{" "}
                {paginatedStudents.length} students
              </div>
            </>
          )}
        </div>
      </section>
      <Footer />
    </main>
  );
}
